import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import React, { useCallback, useMemo, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@/elements/components/utils/convex-api";
import { useQuery } from "@tanstack/react-query";
import { Avatar } from "@/elements/components/ui/avatar";
import { Calendar } from "react-native-calendars";
import LeftArrowIcon from "@/assets/svgs/left-arrow-icon";
import RightArrowIcon from "@/assets/svgs/right-arrow-icon";
import { addDays, format } from "date-fns";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import { Id } from "@teachloop/backend/_generated/dataModel";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import {
  TimeSlotsSelectSheetPayload,
  TimeSlotsSelectSheetResult,
} from "@/elements/components/sheets/time-slots-sheet";
import { Select } from "@/elements/components/ui/select";
import { CheckboxInput } from "@/elements/components/ui/checkbox-input";
import goBack from "@/elements/components/utils/go-back";

interface RecurringSlot {
  startTimeUTC: number;
  endTimeUTC: number;
  isAvailable: boolean;
}

function LessonTimingsScreen() {
  const fmt = useFormatDate();
  const { childId, introCall, tutorId } = useLocalSearchParams<{
    childId: string;
    introCall: string;
    tutorId: string;
  }>();
  const { data: children = [] } = useQuery({
    ...convexQuery(api.parent.home.queries.children, {}),
  });
  const child = (children ?? []).find((child) => child._id === childId);
  const router = useRouter();
  const [selectedDate, setSelectedDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | number>("");
  const [selectedDuration, setSelectedDuration] = useState<60 | 90 | 120>(60);
  const [isRecurringMode, setIsRecurringMode] = useState(false);
  const [selectedRecurringSlots, setSelectedRecurringSlots] = useState<
    number[]
  >([]);
  const { open: openTimeSlotsSelectSheet } = useSheet<
    TimeSlotsSelectSheetPayload,
    TimeSlotsSelectSheetResult
  >(SheetIds.TIME_SLOTS_SELECT);
  const { open: openDurationSelectSheet } = useSheet<
    TimeSlotsSelectSheetPayload,
    TimeSlotsSelectSheetResult
  >(SheetIds.TIME_SLOTS_SELECT);
  const markedDates = {
    [selectedDate]: {
      selected: true,
      selectedColor: "#2C7A7B",
    },
  };
  const { data: availability = [] } = useQuery({
    ...convexQuery(api.parent.bookLesson.queries.getTutorAvailabilityV3, {
      selectedDateUTC: new Date(selectedDate + "T12:00:00").getTime(),
      increments: 15,
      slotDurationMinutes: introCall ? 15 : selectedDuration,
      tutorId: tutorId as Id<"tutorProfiles">,
    }),
    enabled: !!tutorId,
  });

  const { data: recurringAvailability = [] } = useQuery({
    ...convexQuery(api.parent.bookLesson.queries.getRecurringAvailability, {
      tutorId: tutorId as Id<"tutorProfiles">,
      startTimeUTC: typeof selectedTimeSlot === "number" ? selectedTimeSlot : 0,
      slotDurationMinutes: selectedDuration,
      weeksCount: 12,
    }),
    enabled: !!tutorId && !!selectedTimeSlot && !introCall && isRecurringMode,
  });

  const availableRecurringSlots = useMemo(() => {
    return recurringAvailability.filter(
      (slot: RecurringSlot) => slot.isAvailable
    );
  }, [recurringAvailability]);

  const handleToggleRecurringSlot = useCallback((startTimeUTC: number) => {
    setSelectedRecurringSlots((prev) => {
      if (prev.includes(startTimeUTC)) {
        return prev.filter((t) => t !== startTimeUTC);
      }
      return [...prev, startTimeUTC];
    });
  }, []);

  const handleSelectAllRecurringSlots = useCallback(() => {
    const allAvailable = availableRecurringSlots.map(
      (slot: RecurringSlot) => slot.startTimeUTC
    );
    setSelectedRecurringSlots(allAvailable);
  }, [availableRecurringSlots]);

  const handleDeselectAllRecurringSlots = useCallback(() => {
    setSelectedRecurringSlots([]);
  }, []);

  const allAvailableSelected = useMemo(() => {
    if (availableRecurringSlots.length === 0) return false;
    return availableRecurringSlots.every((slot: RecurringSlot) =>
      selectedRecurringSlots.includes(slot.startTimeUTC)
    );
  }, [availableRecurringSlots, selectedRecurringSlots]);

  const handleDurationSelectSheetOpen = useCallback(async () => {
    const result = await openDurationSelectSheet({
      options: [
        { label: "60 minutes", value: 60 },
        { label: "90 minutes", value: 90 },
        { label: "120 minutes", value: 120 },
      ],
      preSelectedOption: selectedDuration,
    });
    if (result?.selectedOption) {
      setSelectedDuration(result.selectedOption as 60 | 90 | 120);
      setSelectedTimeSlot("");
    }
  }, [openDurationSelectSheet, selectedDuration]);

  const handleTimeSlotsSelectSheetOpen = useCallback(async () => {
    const result = await openTimeSlotsSelectSheet({
      options:
        availability?.map((timeSlot) => ({
          label: fmt(timeSlot.startTimeUTC, "H:mm"),
          value: timeSlot.startTimeUTC,
        })) ?? [],
      preSelectedOption: selectedTimeSlot,
    });
    if (result?.selectedOption) {
      setSelectedTimeSlot(result.selectedOption);
    }
  }, [openTimeSlotsSelectSheet, availability, selectedTimeSlot]);

  return (
    <View className="flex-1 bg-teachLoop-creamBase gap-6">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="gap-8">
              <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-dark">
                {introCall ? "Book a free intro" : "Book a lesson"}
              </Text>

              <View className="flex-row items-center gap-2">
                <Text className="font-geologica-medium font-medium text-[16px] leading-[1.5] text-dark">
                  Booking for:
                </Text>
                <View className="flex-row items-center gap-2">
                  <Avatar size="sm">
                    <Avatar.Content
                      size="sm"
                      labelSize="sm"
                      label={child?.name ?? ""}
                      source={
                        child?.imageUrl ? { uri: child.imageUrl } : undefined
                      }
                    />
                  </Avatar>
                  <Text className="font-geologica-medium text-gray-800 text-[16px] leading-[1.5]">
                    {child?.name ?? ""}
                  </Text>
                </View>
              </View>

              <View className="gap-4">
                <Text className="text-base leading-[1.5] font-geologica-medium font-medium text-teachLoop-textPrimary">
                  Select day and time
                </Text>
                <Calendar
                  showSixWeeks
                  style={{
                    backgroundColor: "transparent",
                  }}
                  initialDate={selectedDate}
                  minDate={new Date().toISOString().split("T")[0]}
                  maxDate={addDays(new Date(), 90).toISOString().split("T")[0]}
                  onDayPress={(day) => {
                    setSelectedDate(day.dateString);
                    setSelectedTimeSlot("");
                  }}
                  markedDates={markedDates}
                  theme={{
                    calendarBackground: "transparent",
                    textMonthFontFamily: "Geologica_500Medium",
                    textDayFontFamily: "Geologica_400Regular",
                    textDayHeaderFontFamily: "Geologica_500Medium",
                    weekVerticalMargin: 4,
                    textMonthFontSize: 16,
                    textDayFontSize: 14,
                    textDayHeaderFontSize: 14,
                    monthTextColor: "#000000",
                    dayTextColor: "#1A202C",
                    textSectionTitleColor: "#000000",
                    selectedDayTextColor: "#ffffff",
                    selectedDayBackgroundColor: "#2C7A7B",
                    todayTextColor: "#1A202C",
                  }}
                  renderArrow={(direction) => {
                    return direction === "left" ? (
                      <LeftArrowIcon pathClassName="stroke-teachLoop-textPrimary" />
                    ) : (
                      <RightArrowIcon pathClassName="stroke-teachLoop-textPrimary" />
                    );
                  }}
                />
              </View>

              {!introCall && (
                <View className="gap-4">
                  <Text className="text-base leading-[1.5] font-geologica-medium font-medium text-teachLoop-textPrimary">
                    Select lesson duration
                  </Text>
                  <Select size="md" onPress={handleDurationSelectSheetOpen}>
                    <Select.Label size="md">
                      {selectedDuration} minutes
                    </Select.Label>
                    <Select.DownArrow />
                  </Select>
                </View>
              )}

              <View className="gap-4">
                <Text className="text-base leading-[1.5] font-geologica-medium font-medium text-teachLoop-textPrimary">
                  Choose an available time slot
                </Text>
                <Select
                  disabled={availability.length === 0}
                  size="md"
                  onPress={handleTimeSlotsSelectSheetOpen}
                >
                  <Select.Label size="md">
                    {selectedTimeSlot
                      ? fmt(Number(selectedTimeSlot), "H:mm")
                      : "Select an available time slot"}
                  </Select.Label>
                  <Select.DownArrow />
                </Select>
              </View>

              {!introCall && selectedTimeSlot && (
                <View className="gap-4">
                  <Pressable
                    onPress={() => {
                      setIsRecurringMode(!isRecurringMode);
                      if (!isRecurringMode) {
                        setSelectedRecurringSlots([]);
                      }
                    }}
                    className="flex-row items-center gap-3"
                  >
                    <View
                      className={`w-5 h-5 rounded border ${
                        isRecurringMode
                          ? "bg-teal-500 border-teal-500"
                          : "bg-white border-gray-300"
                      } items-center justify-center`}
                    >
                      {isRecurringMode && (
                        <Text className="text-white text-xs font-bold">✓</Text>
                      )}
                    </View>
                    <Text className="text-base font-geologica-medium text-teachLoop-textPrimary">
                      Book recurring lessons
                    </Text>
                  </Pressable>

                  {isRecurringMode && recurringAvailability.length > 0 && (
                    <View className="gap-3 mt-2">
                      <View className="flex-row justify-between items-center">
                        <Text className="text-sm font-geologica-regular text-gray-600">
                          Next 12 weeks -{" "}
                          {fmt(Number(selectedTimeSlot), "EEEE")}s at{" "}
                          {fmt(Number(selectedTimeSlot), "H:mm")}
                        </Text>
                        <Pressable
                          onPress={
                            allAvailableSelected
                              ? handleDeselectAllRecurringSlots
                              : handleSelectAllRecurringSlots
                          }
                        >
                          <Text className="text-sm font-geologica-medium text-teal-600">
                            {allAvailableSelected
                              ? "Deselect all"
                              : "Select all"}
                          </Text>
                        </Pressable>
                      </View>

                      <View className="gap-2">
                        {recurringAvailability.map((slot: RecurringSlot) => {
                          const isSelected = selectedRecurringSlots.includes(
                            slot.startTimeUTC
                          );
                          const isDisabled = !slot.isAvailable;

                          return (
                            <CheckboxInput
                              key={slot.startTimeUTC}
                              disabled={isDisabled}
                              onPress={() => {
                                if (!isDisabled) {
                                  handleToggleRecurringSlot(slot.startTimeUTC);
                                }
                              }}
                            >
                              <CheckboxInput.Label disabled={isDisabled}>
                                {fmt(slot.startTimeUTC, "EEE, dd MMM yyyy")}{" "}
                                at {fmt(slot.startTimeUTC, "H:mm")}
                                {isDisabled && " (unavailable)"}
                              </CheckboxInput.Label>
                              <CheckboxInput.Icon checked={isSelected} />
                            </CheckboxInput>
                          );
                        })}
                      </View>

                      {selectedRecurringSlots.length > 0 && (
                        <Text className="text-sm font-geologica-regular text-gray-600">
                          {selectedRecurringSlots.length} lesson(s) selected
                        </Text>
                      )}
                    </View>
                  )}
                </View>
              )}

              <Button
                className="w-full"
                size="md"
                intent="primary"
                disabled={
                  !selectedTimeSlot ||
                  (isRecurringMode && selectedRecurringSlots.length === 0)
                }
                onPress={() => {
                  if (isRecurringMode && selectedRecurringSlots.length > 0) {
                    const slotsParam = encodeURIComponent(
                      JSON.stringify(selectedRecurringSlots)
                    );
                    router.push(
                      `/(app)/booking/lesson-details?childId=${childId}&tutorId=${tutorId}&timeSlot=${selectedTimeSlot}&duration=${selectedDuration}&recurring=true&recurringSlots=${slotsParam}`
                    );
                  } else {
                    router.push(
                      `/(app)/booking/lesson-details?childId=${childId}&tutorId=${tutorId}&timeSlot=${selectedTimeSlot}&duration=${selectedDuration}&${introCall ? "introCall=true" : ""}`
                    );
                  }
                }}
              >
                <Button.Text className="text-[16px]">
                  {isRecurringMode && selectedRecurringSlots.length > 0
                    ? `Continue (${selectedRecurringSlots.length} lessons)`
                    : "Continue"}
                </Button.Text>
              </Button>
            </View>
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default LessonTimingsScreen;
